import React, { useMemo, useCallback, useRef, useLayoutEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from 'recharts';
import { fmtMoneyWithDecimals } from '../../utils/formatters';
import { ChartFrame } from '../charts/ChartFrame';
import { buildDailyCumulative } from '../../lib/trades/authoritativeTradeData';
// CustomTooltip removed (not used here)

interface CumulativeDrawdownWidgetProps {
  dailyData: Array<{ date: number; drawdown: number }>;

  isDark: boolean;
}

export const CumulativeDrawdownWidget: React.FC<CumulativeDrawdownWidgetProps> = React.memo(({ dailyData, isDark }) => {
  const hasData = Array.isArray(dailyData) && dailyData.length > 0;

  // Build cumulative series from the provided daily data (some callers provide `cum`, others may provide `pnl`)
  const cumSeries = useMemo(() => buildDailyCumulative(dailyData.map(d => ({ date: d.date as any, pnl: (d as any).cum ?? (d as any).pnl ?? 0 })), false), [dailyData]);

  // Compute running peak and drawdown (peak - current cum). Produce both dollar and percent
  // drawdown values. Percent drawdown is negative (0 at peak, negative while underwater).
  const dataMemo = useMemo(() => {
    if (!cumSeries || cumSeries.length === 0) return [] as { ts: number; value: number; pct: number; cum: number }[];
    let peak = -Infinity;
    const out: { ts: number; value: number; pct: number; cum: number }[] = [];
    for (const pt of cumSeries) {
      const v = typeof pt.value === 'number' && !Number.isNaN(pt.value) ? pt.value : 0;
      peak = Math.max(peak, v);
      const dd = peak - v; // dollars underwater (>= 0)
      const pct = peak > 0 ? -((dd / peak) * 100) : 0; // negative percent, 0 at peak
      out.push({ ts: pt.ts, value: dd, pct, cum: v });

    }
    return out;
  }, [cumSeries]);

  const day = 24 * 60 * 60 * 1000;
  const [xmin, xmax] = [dataMemo[0]?.ts ?? 0, dataMemo[dataMemo.length - 1]?.ts ?? 0];
  const ticksMemo = useMemo(() => {
    if (!xmin || !xmax || xmax <= xmin) return [] as number[];
    const span = Math.max(day, xmax - xmin);
    const divisions = 4; // 5 ticks
    const step = span / divisions;
    const ticks: number[] = [];
    for (let i = 0; i <= divisions; i++) {
      const raw = xmin + Math.round(i * step);
      const d = new Date(raw);
      const snapped = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
      ticks.push(Math.max(xmin, Math.min(snapped, xmax)));
    }
    return Array.from(new Set(ticks)).sort((a, b) => a - b);
  }, [xmin, xmax]);

  const gridColor = isDark ? "#404040" : "#dddddd";
  const drawdownStroke = isDark ? '#ff8080' : '#ff0000';
  const drawdownFill = isDark ? 'rgba(255, 128, 128, 0.2)' : 'rgba(255, 0, 0, 0.3)';
  // Remove any colored background behind the chart in dark mode; keep transparent background.
  const chartBg = 'transparent';
  // Ensure rightmost date label is not clipped: reduce left inset so chart draws closer to the % labels
  // Reduced left from 5 -> 0 to remove excess left padding. Also reduce right gutter by 4px to tighten layout.
  // Increase top margin to 8 to match other chart presets so top labels (like the MDD label)
  // align vertically with chart plotting area.
  const margin = useMemo(() => ({ top: 8, right: 16, bottom: 10, left: 0 }), []);
  const domainMemo = useMemo(() => [xmin, xmax] as [number, number], [xmin, xmax]);
  const xTickStyle = useMemo(() => ({ fontSize: 12, fontFamily: 'ui-sans-serif, system-ui', fill: isDark ? '#9ca3af' : '#374151' }), [isDark]);
  const axisLineProps = useMemo(() => ({ shapeRendering: 'crispEdges' as const }), []);
  // Show dates as MM/DD (e.g. 11/12) using UTC to match tooltip
  const xTickFormatter = useCallback((ms: number) => {
    const d = new Date(ms);
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${mm}/${dd}`;
  }, []);

  const DrawdownTooltip: React.FC<any> = ({ active, payload, isDark }: any) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload as { ts: number; value: number; pct: number; cum: number };
  // Tooltip date as MM/DD/YYYY using UTC components to avoid timezone shifts
  const d = new Date(p.ts);
  const dateStr = `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;
    const bg = isDark ? 'rgba(17,24,39,0.95)' : 'rgba(255,255,255,0.95)';
    const color = isDark ? '#e5e7eb' : '#111827';
    const drawdownStroke = isDark ? '#f87171' : '#dc2626';
    const pctStr = p.pct.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%';
    return (
      <div style={{ background: bg, color, padding: 8, borderRadius: 6, minWidth: 160, boxShadow: '0 4px 12px rgba(2,6,23,0.6)' }}>
        <div style={{ fontSize: 12, marginBottom: 6 }}>{dateStr}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: drawdownStroke }}>Drawdown: {pctStr} ({fmtMoneyWithDecimals(p.value, 0)})</div>
      </div>
    );
  };

  // Attach a ref to the outer widget container and measure the header's left position
  // so we can align Y-axis percent labels with the header's left edge.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [yLabelX, setYLabelX] = useState<number | null>(null);

  useLayoutEffect(() => {
    const compute = () => {
      const el = containerRef.current;
      if (!el) return;
      const widget = el.closest('.widget-card') as HTMLElement | null;
      const header = widget?.querySelector('h3, h2') as HTMLElement | null;
      const chartRect = el.getBoundingClientRect();
      if (header) {
        const headerRect = header.getBoundingClientRect();
        const offset = Math.round(headerRect.left - chartRect.left);
        // Add a small inset so text isn't flush against edge
        const inset = 0;
        setYLabelX(Math.max(0, offset + inset));
      } else {
        setYLabelX(8);
      }
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  // Adjusted Y-axis label alignment: align horizontally using measured header offset
  // and vertically center labels on the tick coordinate to match grid/points.
  const renderYTick = useCallback(({ x, y, payload }: any) => {
    const value = payload?.value;
    // Use the axis coordinate provided by Recharts and right-align the label
    // so its right edge is 4px from the axis line.
    const xPos = (typeof x === 'number') ? (x - 4) : (typeof yLabelX === 'number' ? yLabelX : x);
    return (
      <text x={xPos} y={y} fill={isDark ? '#9ca3af' : '#374151'} fontSize={12} textAnchor="end" dominantBaseline="middle">
        {value}%
      </text>
    );
  }, [isDark, yLabelX]);

  return (
    <div className="relative z-0 isolation-isolate bg-transparent transition-none chart-stable" ref={containerRef}>
      {!hasData ? (
        <div className="h-full flex items-center justify-center rounded-xl border border-dashed border-zinc-200 text-sm text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
          Import Trades to see the widget
        </div>
      ) : (
      <ChartFrame style={{ background: chartBg }}>
        {({ width, height }: { width: number; height: number }) => (
          <AreaChart width={width} height={height} data={dataMemo} margin={margin}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} strokeOpacity={0.5} />
            <YAxis
              dataKey="pct"
              type="number"
              tickFormatter={(v: number) => `${v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
              tick={renderYTick as any}
              axisLine={true}
              tickLine={true}
              // Reduce reserved Y-axis width from 56 -> 44 -> 42 -> 40 to free up left space
              width={40}
              tickMargin={0}
            />
            <XAxis
              dataKey="ts"
              type="number"
              domain={domainMemo}
              ticks={ticksMemo}
              interval={0}
              allowDecimals={false}
              tickMargin={8}
              height={28}
              tickLine={true}
              axisLine={axisLineProps}
              tick={xTickStyle as any}
              tickFormatter={xTickFormatter}
            />
            <Tooltip isAnimationActive={false} content={(props) => <DrawdownTooltip {...props} isDark={isDark} />} />
            <ReferenceLine y={0} stroke={isDark ? "#aaaaaa" : "#888888"} strokeWidth={1} strokeDasharray="0" />
            {/* MDD reference line and label */}
            {dataMemo.length > 0 && (() => {
              const mddPoint = dataMemo.reduce((acc, cur) => (cur.pct < acc.pct ? cur : acc), dataMemo[0]);
              const mddPct = mddPoint.pct;
              const label = `MDD ${mddPct.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
              const CustomLabel = ({ viewBox }: any) => {
                const { x, y } = viewBox;
                const labelText = label;
                const measureText = (t: string) => t.length * 7;
                const padX = 12; // horizontal padding inside pill (px)
                const measured = Math.ceil(measureText(labelText));
                const labelWidth = Math.max(40, measured + padX);
                const labelHeight = 22;
                const rx = Math.round(labelHeight / 2);
                const rectX = x + 5;
                // Move the pill up slightly so it sits above the reference line
                const rectY = (y - labelHeight / 2) - 8;
                return (
                  <g>
                    <rect x={rectX} y={rectY} width={labelWidth} height={labelHeight} fill={isDark ? '#c00' : '#ff0000'} rx={rx} />
                    <text x={rectX + labelWidth / 2} y={rectY + labelHeight / 2} fill="white" textAnchor="middle" dominantBaseline="middle" fontSize={12} fontWeight={500}>
                      {labelText}
                    </text>
                  </g>
                );
              };
              return <ReferenceLine y={mddPct} stroke={isDark ? '#ff8080' : '#ff0000'} strokeDasharray="4 4" label={<CustomLabel />} />;
            })()}
            {/* Drawdown area in red */}
            <Area type="monotone" dataKey="pct" dot={{ r: 2, fill: drawdownStroke, strokeWidth: 0 }} isAnimationActive={false} animationDuration={0} stroke={drawdownStroke} fill={drawdownFill} />
          </AreaChart>
        )}
      </ChartFrame>
      )}
    </div>
  );
}, (prev, next) => {
  if (prev.isDark !== next.isDark) return false;
  const a = prev.dailyData, b = next.dailyData;
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const la = a[a.length - 1]?.date;
  const lb = b[b.length - 1]?.date;
  return la === lb;
});
